import { useState, useEffect, useMemo } from 'react';
import { countRemainingSolutions } from '../engine/solver';
import type { Hint } from '../engine/types';

interface RemainingSolutionsResult {
  remaining: number;
  isCalculating: boolean;
}

interface VisibleHint {
  digits: number[];
  correct_placed: number;
  correct_misplaced: number;
}

/**
 * Computes remaining solutions from visible hints.
 * Excludes ghost hints (which the client cannot distinguish — this is intentional,
 * ghost hints cause the counter to show incorrect numbers, adding to the deception).
 *
 * Updates on each new hint reveal or reserve hint draw.
 * For code length >= 4, consider running in a Web Worker to avoid blocking the main thread.
 */
export function useRemainingSolutions(
  codeLength: number,
  hints: ReadonlyArray<VisibleHint>,
  digitRange: [number, number] = [0, 9],
): RemainingSolutionsResult {
  const [remaining, setRemaining] = useState<number>(0);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);

  const hintsKey = useMemo(
    () =>
      JSON.stringify(
        hints.map((h) => ({
          d: h.digits,
          p: h.correct_placed,
          m: h.correct_misplaced,
        })),
      ),
    [hints],
  );

  useEffect(() => {
    if (hints.length === 0) {
      const digitCount = digitRange[1] - digitRange[0] + 1;
      const totalSpace = Math.pow(digitCount, codeLength);
      setRemaining(totalSpace);
      setIsCalculating(false);
      return;
    }

    const engineHints: Hint[] = hints.map((h) => ({
      digits: h.digits,
      correct_placed: h.correct_placed,
      correct_misplaced: h.correct_misplaced,
    }));

    if (codeLength >= 4) {
      setIsCalculating(true);
      const timeoutId = setTimeout(() => {
        const count = countRemainingSolutions(codeLength, engineHints, digitRange);
        setRemaining(count);
        setIsCalculating(false);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      setIsCalculating(true);
      const count = countRemainingSolutions(codeLength, engineHints, digitRange);
      setRemaining(count);
      setIsCalculating(false);
    }
  }, [codeLength, hintsKey, digitRange]);

  return { remaining, isCalculating };
}
